import { DocsLayout, DOC_SECTIONS } from "@/components/docs-layout";
import { Link } from "react-router-dom";
import {
  BookOpen,
  TrendingUp,
  Coins,
  LayoutDashboard,
  Shield,
  HelpCircle,
  ChevronRight,
} from "lucide-react";

const CATEGORY_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  "Getting Started": BookOpen,
  "Trading Guides": TrendingUp,
  "Markets & Fees": Coins,
  "Platform Features": LayoutDashboard,
  "Security & Best Practices": Shield,
};

const CATEGORY_DESCRIPTIONS: Record<string, string> = {
  "Getting Started": "Everything you need to start trading on Nordex",
  "Trading Guides": "Essential knowledge for perpetual futures trading",
  "Markets & Fees": "Platform economics and trading costs",
  "Platform Features": "Advanced features and analysis tools",
  "Security & Best Practices": "Stay safe while trading",
};

export const DocsIndex = () => {
  return (
    <DocsLayout fullWidth>
      {/* Hero */}
      <div className="mb-12">
        <h1 className="text-3xl font-bold mb-3 tracking-tight">
          Nordex Documentation
        </h1>
        <p className="text-[15px] leading-relaxed text-muted-foreground max-w-xl">
          Welcome to the Nordex documentation. Learn how to trade perpetual
          futures with leverage, manage your positions, and make the most of our
          platform.
        </p>
      </div>

      {/* Category Cards */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 mb-12">
        {DOC_SECTIONS.map((section) => {
          const Icon =
            CATEGORY_ICONS[section.title] || HelpCircle;
          const description =
            CATEGORY_DESCRIPTIONS[section.title] || "";

          return (
            <div
              key={section.title}
              className="border border-border rounded-lg bg-card overflow-hidden group hover:border-foreground/10 transition-colors"
            >
              {/* Card Header */}
              <div className="p-5 border-b border-border/60">
                <div className="flex items-start gap-3 mb-2">
                  <div className="p-2 rounded-md bg-accent">
                    <Icon className="h-4 w-4 text-foreground/70" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-sm font-semibold mb-1">
                      {section.title}
                    </h2>
                    {description && (
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {description}
                      </p>
                    )}
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">
                  {section.items.length}{" "}
                  {section.items.length === 1 ? "article" : "articles"}
                </div>
              </div>

              {/* Article List */}
              <div className="p-3">
                <ul className="space-y-0.5">
                  {section.items.map((article) => (
                    <li key={article.href}>
                      <Link
                        to={article.href}
                        className="flex items-center justify-between py-1.5 px-2.5 rounded-md text-[13px] text-muted-foreground hover:text-foreground hover:bg-accent transition-colors group/item"
                      >
                        <span>{article.label}</span>
                        <ChevronRight className="h-3 w-3 opacity-0 group-hover/item:opacity-100 transition-opacity" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Start CTA */}
      <div className="p-6 border border-border rounded-lg bg-card">
        <h3 className="text-sm font-semibold mb-2">Ready to get started?</h3>
        <p className="text-[13px] text-muted-foreground mb-4">
          New to Nordex? Start with our getting started guide to learn the
          basics.
        </p>
        <Link
          to="/welcome"
          className="inline-flex items-center gap-2 text-[13px] font-medium text-foreground hover:underline underline-offset-2"
        >
          Start here
          <ChevronRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </DocsLayout>
  );
};
